import type { SectionResponse, SubJourneyResponse } from '@manasil/shared';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { resolveMediaUrl } from '../lib/api.js';
import {
  BANNER_SPEC,
  IMAGE_ACCEPT,
  inspectImage,
  removeSectionImage,
  uploadSectionImage,
} from '../lib/image.js';
import {
  useCreateSection,
  useCreateSubJourney,
  useDeleteSection,
  useDeleteSubJourney,
  useInvalidateCollection,
  useReorderSections,
  useSections,
  useSubJourneys,
  useUpdateSection,
  useUpdateSubJourney,
} from '../lib/queries.js';
import { Empty, Field, Loading, Modal, errorMessage, formatDuration, useToast } from './ui.js';

/**
 * The body of the editor for a COLLECTION journey: its sections, and under each one its
 * sub-journeys. Episodes live a level down, on their own page — an adhikaram's ten kurals
 * are the thing an editor works on for a while, and they deserve the whole screen.
 *
 * For Thirukkural this is three sections and 133 adhikarams, so the list is long but
 * flat: every section is open, every sub-journey is one row.
 */
export function CollectionEditor({ journeyId }: { journeyId: string }) {
  const { data: sections, isLoading } = useSections(journeyId);
  const { data: subJourneys } = useSubJourneys(journeyId);
  const [addingSection, setAddingSection] = useState(false);

  if (isLoading || !sections) {
    return (
      <div className="card">
        <Loading />
      </div>
    );
  }

  const bySection = new Map<string, SubJourneyResponse[]>();
  for (const subJourney of subJourneys ?? []) {
    const list = bySection.get(subJourney.sectionId) ?? [];
    list.push(subJourney);
    bySection.set(subJourney.sectionId, list);
  }

  const episodeCount = sections.reduce((sum, section) => sum + section.episodeCount, 0);
  const readyCount = sections.reduce((sum, section) => sum + section.readyCount, 0);

  return (
    <>
      <div className="card">
        <div className="card-head">
          <div>
            <h3>Sections</h3>
            <p className="hint">
              {sections.length} sections · {subJourneys?.length ?? 0} sub-journeys · {readyCount} of{' '}
              {episodeCount} episodes have audio. A user plays these in any order.
            </p>
          </div>
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={() => setAddingSection(true)}
          >
            Add section
          </button>
        </div>

        {sections.length === 0 ? (
          <Empty
            mark="◫"
            title="No sections yet"
            hint="Thirukkural has three: அறம், பொருள் and இன்பம். Each holds its adhikarams."
          />
        ) : (
          <div className="section-list">
            {sections.map((section, index) => (
              <SectionBlock
                key={section.id}
                journeyId={journeyId}
                section={section}
                subJourneys={bySection.get(section.id) ?? []}
                allSectionIds={sections.map((row) => row.id)}
                isFirst={index === 0}
                isLast={index === sections.length - 1}
              />
            ))}
          </div>
        )}
      </div>

      {addingSection ? (
        <SectionModal journeyId={journeyId} onClose={() => setAddingSection(false)} />
      ) : null}
    </>
  );
}

/* ─────────────────────── one section, with its sub-journeys ─────────────────────── */

function SectionBlock({
  journeyId,
  section,
  subJourneys,
  allSectionIds,
  isFirst,
  isLast,
}: {
  journeyId: string;
  section: SectionResponse;
  subJourneys: SubJourneyResponse[];
  allSectionIds: string[];
  isFirst: boolean;
  isLast: boolean;
}) {
  const reorder = useReorderSections(journeyId);
  const remove = useDeleteSection(journeyId);
  const invalidate = useInvalidateCollection(journeyId);
  const { push } = useToast();
  const [editing, setEditing] = useState(false);
  const [addingSubJourney, setAddingSubJourney] = useState(false);
  const [imageBusy, setImageBusy] = useState(false);

  /**
   * The section image, uploaded on choose rather than behind a Save: the thumbnail IS the
   * control, and there is nothing else on this row to save it together with. Shape and
   * size are checked first, so a wrong file is refused before it leaves the browser.
   */
  async function chooseImage(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setImageBusy(true);
    try {
      const check = await inspectImage(file, BANNER_SPEC);
      URL.revokeObjectURL(check.previewUrl);
      if (check.problems.length > 0) {
        push(check.problems.join(' '), 'err');
        return;
      }
      await uploadSectionImage(section.id, file, check, () => undefined);
      invalidate();
      push(`Image set on "${section.title}"`);
    } catch (caught) {
      push(errorMessage(caught), 'err');
    } finally {
      setImageBusy(false);
    }
  }

  async function clearImage() {
    if (!confirm(`Remove the image from "${section.title}"? The card falls back to its icon.`))
      return;
    setImageBusy(true);
    try {
      await removeSectionImage(section.id);
      invalidate();
      push('Image removed');
    } catch (caught) {
      push(errorMessage(caught), 'err');
    } finally {
      setImageBusy(false);
    }
  }

  function move(delta: -1 | 1) {
    const index = allSectionIds.indexOf(section.id);
    const target = index + delta;
    if (target < 0 || target >= allSectionIds.length) return;
    const next = [...allSectionIds];
    next.splice(index, 1);
    next.splice(target, 0, section.id);
    reorder.mutate(next, { onError: (caught) => push(errorMessage(caught), 'err') });
  }

  function confirmRemove() {
    const what =
      subJourneys.length > 0
        ? `\n\nIts ${subJourneys.length} sub-journeys and their ${section.episodeCount} episodes go with it, audio included.`
        : '';
    if (!confirm(`Delete the section "${section.title}"?${what}`)) return;
    remove.mutate(section.id, {
      onSuccess: () => push(`Removed "${section.title}"`),
      onError: (caught) => push(errorMessage(caught), 'err'),
    });
  }

  return (
    <section className="section-block">
      <header className="section-head">
        {/* The image is the icon's replacement: the app shows it on the section card when
            set, and the emoji otherwise. Clicking the tile picks a new file. */}
        <label
          className="section-icon section-image"
          title={section.hasImage ? 'Replace image' : 'Add an image (1080 × 1080)'}
          aria-busy={imageBusy}
        >
          {section.imageUrl ? (
            <img src={resolveMediaUrl(section.imageUrl)} alt="" />
          ) : (
            <span aria-hidden="true">{section.icon || '◫'}</span>
          )}
          <input
            type="file"
            accept={IMAGE_ACCEPT}
            hidden
            onChange={chooseImage}
            disabled={imageBusy}
          />
        </label>
        <div className="grow">
          <div className="section-title">
            <span className="section-order nums">{section.order}</span>
            {section.title}
          </div>
          <div className="hint">
            {section.subJourneyCount} sub-journeys · {section.readyCount} of {section.episodeCount}{' '}
            episodes with audio
            {section.description ? ` · ${section.description}` : ''}
          </div>
        </div>
        <div className="row" style={{ gap: 6 }}>
          <button
            type="button"
            className="btn btn-icon"
            onClick={() => move(-1)}
            disabled={isFirst || reorder.isPending}
            aria-label={`Move "${section.title}" up`}
            title="Move up"
          >
            ↑
          </button>
          <button
            type="button"
            className="btn btn-icon"
            onClick={() => move(1)}
            disabled={isLast || reorder.isPending}
            aria-label={`Move "${section.title}" down`}
            title="Move down"
          >
            ↓
          </button>
          {section.hasImage ? (
            <button
              type="button"
              className="btn btn-sm"
              onClick={clearImage}
              disabled={imageBusy}
              title="Remove the image"
            >
              No image
            </button>
          ) : null}
          <button type="button" className="btn btn-sm" onClick={() => setEditing(true)}>
            Edit
          </button>
          <button
            type="button"
            className="btn btn-sm btn-primary"
            onClick={() => setAddingSubJourney(true)}
          >
            Add sub-journey
          </button>
          <button
            type="button"
            className="btn btn-icon btn-danger"
            onClick={confirmRemove}
            disabled={remove.isPending}
            aria-label={`Delete "${section.title}"`}
            title="Delete"
          >
            ✕
          </button>
        </div>
      </header>

      {subJourneys.length === 0 ? (
        <p className="hint section-empty">
          No sub-journeys yet. For Thirukkural, a sub-journey is one adhikaram — ten kurals.
        </p>
      ) : (
        <ol className="subjourney-list">
          {subJourneys.map((subJourney) => (
            <SubJourneyRow key={subJourney.id} journeyId={journeyId} subJourney={subJourney} />
          ))}
        </ol>
      )}

      {editing ? (
        <SectionModal journeyId={journeyId} section={section} onClose={() => setEditing(false)} />
      ) : null}
      {addingSubJourney ? (
        <SubJourneyModal
          journeyId={journeyId}
          sectionId={section.id}
          onClose={() => setAddingSubJourney(false)}
        />
      ) : null}
    </section>
  );
}

function SubJourneyRow({
  journeyId,
  subJourney,
}: {
  journeyId: string;
  subJourney: SubJourneyResponse;
}) {
  const remove = useDeleteSubJourney(journeyId);
  const { push } = useToast();
  const [editing, setEditing] = useState(false);
  const ready = subJourney.episodeCount > 0 && subJourney.readyCount === subJourney.episodeCount;

  function confirmRemove() {
    const what =
      subJourney.episodeCount > 0
        ? `\n\nIts ${subJourney.episodeCount} episodes go with it, audio included.`
        : '';
    if (!confirm(`Delete ${subJourney.number} · "${subJourney.title}"?${what}`)) return;
    remove.mutate(subJourney.id, {
      onSuccess: () => push(`Removed "${subJourney.title}"`),
      onError: (caught) => push(errorMessage(caught), 'err'),
    });
  }

  return (
    <li className="subjourney-row">
      <span className="subjourney-number nums">{subJourney.number}</span>
      <Link
        to={`/journeys/${journeyId}/sub-journeys/${subJourney.id}`}
        className="row-link grow truncate"
      >
        {subJourney.title}
      </Link>
      <span className={`pill ${ready ? 'pill-ok' : 'pill-missing'}`}>
        {subJourney.episodeCount === 0
          ? 'No episodes'
          : `${subJourney.readyCount} / ${subJourney.episodeCount} audio`}
      </span>
      {subJourney.durationSec > 0 ? (
        <span className="hint nums">{formatDuration(subJourney.durationSec)}</span>
      ) : null}
      <button
        type="button"
        className="btn btn-icon"
        onClick={() => setEditing(true)}
        aria-label={`Edit "${subJourney.title}"`}
        title="Edit"
      >
        ✎
      </button>
      <button
        type="button"
        className="btn btn-icon btn-danger"
        onClick={confirmRemove}
        disabled={remove.isPending}
        aria-label={`Delete "${subJourney.title}"`}
        title="Delete"
      >
        ✕
      </button>

      {editing ? (
        <SubJourneyModal
          journeyId={journeyId}
          sectionId={subJourney.sectionId}
          subJourney={subJourney}
          onClose={() => setEditing(false)}
        />
      ) : null}
    </li>
  );
}

/* ─────────────────────── section form (create and edit) ─────────────────────── */

function SectionModal({
  journeyId,
  section,
  onClose,
}: {
  journeyId: string;
  section?: SectionResponse;
  onClose: () => void;
}) {
  const create = useCreateSection(journeyId);
  const update = useUpdateSection(journeyId);
  const { push } = useToast();
  const [title, setTitle] = useState(section?.title ?? '');
  const [description, setDescription] = useState(section?.description ?? '');
  const [icon, setIcon] = useState(section?.icon ?? '');
  const [error, setError] = useState<string | null>(null);
  const pending = create.isPending || update.isPending;

  async function submit() {
    setError(null);
    try {
      if (section) {
        await update.mutateAsync({
          sectionId: section.id,
          title: title.trim(),
          description: description.trim(),
          icon: icon.trim(),
        });
        push(`Saved "${title.trim()}"`);
      } else {
        await create.mutateAsync({
          title: title.trim(),
          description: description.trim(),
          icon: icon.trim(),
        });
        push(`Added "${title.trim()}"`);
      }
      onClose();
    } catch (caught) {
      setError(errorMessage(caught));
    }
  }

  return (
    <Modal
      title={section ? 'Edit section' : 'Add section'}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={submit}
            disabled={pending || title.trim().length === 0}
          >
            {pending ? 'Saving…' : section ? 'Save changes' : 'Add section'}
          </button>
        </>
      }
    >
      {error ? <div className="banner">{error}</div> : null}

      <div className="grid-2" style={{ gridTemplateColumns: '72px 1fr' }}>
        <Field label="Icon" hint="One emoji">
          <input
            className="input"
            value={icon}
            onChange={(event) => setIcon(event.target.value)}
            placeholder="🌱"
            maxLength={8}
          />
        </Field>
        <Field label="Title">
          <input
            className="input"
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            placeholder="அறம்"
          />
        </Field>
      </div>

      <Field label="Description" hint="One line under the title on the section card.">
        <textarea
          className="textarea"
          value={description}
          onChange={(event) => setDescription(event.target.value)}
          placeholder="Virtue — how to live well with others and oneself."
        />
      </Field>
    </Modal>
  );
}

/* ─────────────────────── sub-journey form (create and edit) ─────────────────────── */

function SubJourneyModal({
  journeyId,
  sectionId,
  subJourney,
  onClose,
}: {
  journeyId: string;
  sectionId: string;
  subJourney?: SubJourneyResponse;
  onClose: () => void;
}) {
  const create = useCreateSubJourney(journeyId);
  const update = useUpdateSubJourney(journeyId);
  const { data: sections } = useSections(journeyId);
  const { push } = useToast();
  const [number, setNumber] = useState(subJourney ? String(subJourney.number) : '');
  const [title, setTitle] = useState(subJourney?.title ?? '');
  const [description, setDescription] = useState(subJourney?.description ?? '');
  const [chosenSection, setChosenSection] = useState(sectionId);
  const [error, setError] = useState<string | null>(null);
  const pending = create.isPending || update.isPending;

  const parsedNumber = number.trim() === '' ? undefined : Number(number);
  const numberOk =
    parsedNumber === undefined || (Number.isInteger(parsedNumber) && parsedNumber >= 1);

  async function submit() {
    setError(null);
    try {
      if (subJourney) {
        await update.mutateAsync({
          subJourneyId: subJourney.id,
          sectionId: chosenSection,
          ...(parsedNumber !== undefined ? { number: parsedNumber } : {}),
          title: title.trim(),
          description: description.trim(),
        });
        push(`Saved "${title.trim()}"`);
      } else {
        const created = await create.mutateAsync({
          sectionId: chosenSection,
          ...(parsedNumber !== undefined ? { number: parsedNumber } : {}),
          title: title.trim(),
          description: description.trim(),
        });
        push(`Added ${created.number} · "${created.title}"`);
      }
      onClose();
    } catch (caught) {
      setError(errorMessage(caught));
    }
  }

  return (
    <Modal
      title={subJourney ? 'Edit sub-journey' : 'Add sub-journey'}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={submit}
            disabled={pending || title.trim().length === 0 || !numberOk}
          >
            {pending ? 'Saving…' : subJourney ? 'Save changes' : 'Add sub-journey'}
          </button>
        </>
      }
    >
      {error ? <div className="banner">{error}</div> : null}

      <div className="grid-2" style={{ gridTemplateColumns: '110px 1fr' }}>
        <Field label="Number" hint={subJourney ? 'Unique in the journey' : 'Blank takes the next'}>
          <input
            className="input nums"
            inputMode="numeric"
            value={number}
            onChange={(event) => setNumber(event.target.value)}
            placeholder="1"
          />
        </Field>
        <Field label="Title">
          <input
            className="input"
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            placeholder="கடவுள் வாழ்த்து"
          />
        </Field>
      </div>

      {sections && sections.length > 1 ? (
        <Field label="Section">
          <select
            className="select"
            value={chosenSection}
            onChange={(event) => setChosenSection(event.target.value)}
          >
            {sections.map((section) => (
              <option key={section.id} value={section.id}>
                {section.order} · {section.title}
              </option>
            ))}
          </select>
        </Field>
      ) : null}

      <Field label="Description" hint="Optional.">
        <textarea
          className="textarea"
          value={description}
          onChange={(event) => setDescription(event.target.value)}
          placeholder="In praise of God — the opening adhikaram."
        />
      </Field>
    </Modal>
  );
}
